import React from 'react';
import { Link } from 'react-router-dom';
import { Info, AlertTriangle } from 'lucide-react';
import NotificationPageLayout, { CodeBlock } from '../components/NotificationPageLayout';
import LangTabs from '../components/LangTabs';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'setting-up-handler', label: 'Setting Up A Handler', children: [
    { id: 'sample-notification-url', label: 'Sample Notification URL' },
    { id: 'sample-handler', label: 'Sample Notification Handler' },
  ]},
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'sample-xml', label: 'Sample XML Notification' },
  { id: 'playbook', label: 'Response Playbook' },
];

const NOTIFICATION_URL_SAMPLE = `http://example.com/Notification-Handler/Chargeback-Fraud-Notification/?apisignature=fraudtransactionnotification&data=<R>...</R>`;

const PHP_SAMPLE = `<?php
// Post Request - Fraud Notification
if(isset($_POST["data"])) //If we have data
{
    if($_POST["apisignature"] == "fraudtransactionnotification") //Fraud Notification
    {
        //Handler code for fraud
        $strData = $_POST["data"]; //Assign the data to variable $strData
        $XMLDataArray = simplexml_load_string($strData); //Used to parse XML at ease
        //...

        /* Obtain information from response fields */
        $ResponseR1 = (string)$XMLDataArray->R1;
        $ResponseR2 = (string)$XMLDataArray->R2;
        //... etc

        //Or loop through the elements
        /* END */

        //More code...
    }
    else if($_POST["apisignature"] == "chargeback") //Chargeback Notification
    {
        //Handler code for chargeback...
    }
}
?>`;

const CSHARP_SAMPLE = `// Post Request - Fraud Notification
[HttpPost]
public IActionResult NotificationURL(string apisignature, string data)
{
    if(data != null) //If we have data
    {
        if(apisignature == "fraudtransactionnotification") //Fraud Notification
        {
            //Handler code for fraud

            /* Parse XML from response */
            XmlDocument xDoc = new XmlDocument();
            xDoc.LoadXml(data);
            /* END */

            /* Obtain information from response fields */
            XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
            XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
            //... etc

            //Or loop through the elements
            /* END */

            //More code...
        }
        else if(apisignature == "chargeback") //Chargeback Notification
        {
            //Handler code for chargeback...
        }
    }

    return View();
}`;

const SAMPLE_XML = `<!-- Sample XML Document, contained inside the \`data=\` parameter -->
<R>
    <R1>5a017421-0a92-4141-a4e9-2fd3969307db</R1>
    <R2>05421215223811034925862</R2>
    <R3>100</R3>
    <R4>EUR</R4>
</R>`;

const FIELDS = [
  { name: 'R1', desc: 'Merchant Transaction Code — your own reference for the original transaction (matches R3 on your Sale request).' },
  { name: 'R2', desc: 'ARN — Acquirer Reference Number identifying the transaction at the scheme level.' },
  { name: 'R3', desc: 'Amount — the amount of the flagged transaction.' },
  { name: 'R4', desc: 'Currency — ISO 4217 alpha currency code (e.g. EUR, GBP, USD).' },
];

const FraudNotification = () => (
  <NotificationPageLayout
    currentId="fraud"
    title="Fraud"
    titleBadge="Notification"
    subtitle="A fraud notification POSTed to your Notification URL when a transaction is flagged as potentially fraudulent — your early-warning signal to void, refund, or hold fulfilment before it escalates to a chargeback."
    toc={TOC}
    mainTestId="fraud-notification-page"
    sidebarTestId="fraud-notification-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        A fraud notification is sent to the merchant's desired Notification URL containing useful information about the transaction if fraud occurs. Below explains the data the merchant can expect to be received in the Notification URL — the merchant can use this data to handle the transaction.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The merchant must submit their chosen Notification URL to eCOMM for it to be whitelisted and enabled for use. The notification will be sent via <strong>HTTP Form Post in XML format</strong> where the merchant should set up a handler to parse the response.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The fraud notification can be identified by using the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">apisignature</code> — if <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">fraud</code> is specified then the following XML document relates to a fraud. The XML document will be transmitted through a field called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code>.
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          Please note that when setting up the handler, the merchant must also cater for the{' '}
          <Link to="/technical-documentation/notifications/chargeback" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">chargeback</Link>{' '}
          notification as the fraud and chargeback notification will be sent to the <strong>same URL</strong>.
        </p>
      </div>
    </section>

    {/* Setting Up A Handler */}
    <section id="setting-up-handler" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Setting Up A Handler</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        It is up to the merchant to decide what may be the best decision when deciding on how to handle the fraud notification. The below example is intended to give the merchant an idea on how this can be achieved.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        If the merchant's notification URL is: <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">http://example.com/Notification-Handler/Chargeback-Fraud-Notification/</code></strong>, the HTTP POST will be sent identical to the below sample URL:
      </p>

      <div id="sample-notification-url" className="scroll-mt-28">
        <CodeBlock code={NOTIFICATION_URL_SAMPLE} lang="http" testid="notification-url-code" />
      </div>

      <p className="text-gray-700 text-sm leading-relaxed mt-4">
        The XML document will be enclosed in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">data</code> parameter which must be parsed in order to pull out the information the merchant may need to proceed.
      </p>
      <p className="text-gray-700 text-sm leading-relaxed mt-3">
        The <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">apisignature</code></strong> will be <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">fraudtransactionnotification</code></strong> in this case.
      </p>

      <div id="sample-handler" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample Notification Handler</h3>
        <LangTabs
          samples={{ php: PHP_SAMPLE, csharp: CSHARP_SAMPLE }}
          testidBase="handler-lang"
        />
      </div>
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The fraud notification will POST an XML document; below will help identify what XML fields are included and what to expect.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4 font-semibold">
        A well-defined XML document response will be returned with the following information:
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="fraud-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-24">Field Name</th>
              <th className="text-left p-3">Description</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79] align-top">{f.name}</td>
                <td className="p-3 align-top">{f.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample XML Notification</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below demonstrates what is contained inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter of the notification.
      </p>
      <CodeBlock code={SAMPLE_XML} lang="xml" testid="fraud-sample-code" />
    </section>

    {/* Playbook (value-add) */}
    <section id="playbook" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Response Playbook</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        A fraud notification is your chance to stop loss before it becomes a chargeback. Ship fulfilment workflows that <strong>block automatically</strong> on fraud alerts until a human has reviewed.
      </p>
      <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
        {[
          'Page an on-call operator the moment a fraud notification is POSTed — email alone is too slow.',
          'Match R1 (Merchant Transaction Code) against your orders table and pause the fulfilment record immediately.',
          'If the transaction is still in Pre-Auth, call Void Pre-Auth to release the frozen funds.',
          'If the transaction has already settled, call Refund before the cardholder raises a chargeback.',
          'Add the device fingerprint, IP and PAN to your internal deny-list so you don\'t re-process the same actor.',
          'Log the R2 ARN for regulatory reporting and to feed your own fraud-analysis dashboard.',
        ].map((t) => (
          <li key={t} className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Remediation actions:</strong> for Pre-Auth transactions call{' '}
          <Link to="/technical-documentation/api-reference/void-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Void Pre-Auth</Link>;
          for already-settled Sales call{' '}
          <Link to="/technical-documentation/api-reference/refund" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Refund</Link>.
          Acting fast is almost always cheaper than defending a chargeback.
        </p>
      </div>
    </section>
  </NotificationPageLayout>
);

export default FraudNotification;
